import { logGreyErrorFromAccounts } from 'owa-account-analytics';
import { isAccountSourceListStoreInitialized } from 'owa-account-source-list-store';
import getAccountDiagnosticDataForMailboxInfo from 'owa-account-source-list-store/lib/utils/getAccountDiagnosticDataForMailboxInfo';
import type {
    AccountActionOrigin,
    AccountSourceProtocolData,
    AccountSourceType,
    BulkTransferPrerequisitesStatus,
    ExportToPstPrerequisitesStatus,
    LicensingMailboxInfo,
    LicensingRemediationResult,
    M365InstallationCheckResult,
    PstPrerequisitesStatus,
} from 'owa-account-source-list-types';
import type {
    AccountAuthType,
    AutomappedMailbox,
    CoprincipalAccount,
} from 'owa-account-storage-types';
import type { AuthUXCallbacks } from 'owa-auth-callbacks';
import { getAccountKeyForMailboxInfo, isValidAccountKey, type MailboxInfo } from 'owa-client-types';
import { errorThatWillCauseAlert, errorThatWillCauseAlertAndThrow } from 'owa-trace';
import { type TraceErrorObject } from 'owa-analytics-types';
import { CreateAccountErrors, type CreateAccountResult, type RemoveAccountResult } from '../types';
import { removeAutomappedSharedMailbox } from './removeAutomappedSharedMailbox';
import { type NewAccountCloudEnvironmentInfo } from 'owa-account-cloud';

/**
 * Defines the interface that the hosting application must implement
 */
export interface PluggableHostAccountSource {
    addM365Account?: (
        correlationId: string,
        email: string,
        userIdentity: string,
        origin: AccountActionOrigin,
        authUxCallbacks?: AuthUXCallbacks,
        isAnotherMailbox?: boolean,
        silentAuthToken?: string,
        skipAccountPolicyChecks?: boolean,
        cloudEnvironmentInfo?: NewAccountCloudEnvironmentInfo
    ) => Promise<CreateAccountResult>;
    addOutlookDotComAccount?: (
        correlationId: string,
        email: string,
        origin: AccountActionOrigin,
        authUxCallbacks?: AuthUXCallbacks,
        silentAuthToken?: string
    ) => Promise<CreateAccountResult>;
    addGoogleAccount?: (
        correlationId: string,
        email: string,
        origin: AccountActionOrigin,
        authUxCallbacks?: AuthUXCallbacks
    ) => Promise<CreateAccountResult>;
    addYahooAccount?: (
        correlationId: string,
        email: string,
        origin: AccountActionOrigin,
        authUxCallbacks?: AuthUXCallbacks
    ) => Promise<CreateAccountResult>;
    addImapAccount?: (
        correlationId: string,
        email: string,
        origin: AccountActionOrigin,
        protocolData: AccountSourceProtocolData[],
        authUxCallbacks?: AuthUXCallbacks
    ) => Promise<CreateAccountResult>;
    addPop3Account?: (
        correlationId: string,
        email: string,
        origin: AccountActionOrigin,
        protocolData: AccountSourceProtocolData[],
        authUxCallbacks?: AuthUXCallbacks
    ) => Promise<CreateAccountResult>;
    addICloudAccount?: (
        correlationId: string,
        email: string,
        origin: AccountActionOrigin,
        protocolData?: AccountSourceProtocolData[],
        authUxCallbacks?: AuthUXCallbacks,
        authType?: 'ICloudBasicAuth' | 'ICloudOAuth'
    ) => Promise<CreateAccountResult>;
    addPstFileAccount?: (
        correlationId: string,
        filepath: string,
        displayName: string,
        origin: AccountActionOrigin,
        authUxCallbacks?: AuthUXCallbacks
    ) => Promise<CreateAccountResult>;
    removeAccount?: (sourceId: string, origin: AccountActionOrigin) => Promise<RemoveAccountResult>;
    removePendingAccount?: (
        emailAddress: string,
        userIdenitity: string,
        origin: AccountActionOrigin,
        accountType: AccountSourceType
    ) => Promise<RemoveAccountResult>;
    disablePendingAccount?: (
        emailAddress: string,
        userIdentity: string,
        origin: AccountActionOrigin,
        accountType: AccountSourceType
    ) => Promise<RemoveAccountResult>;
    createAndAddOutlookDotComAccount?: (
        correlationId: string,
        origin: AccountActionOrigin,
        authUxCallbacks?: AuthUXCallbacks
    ) => Promise<CreateAccountResult>;
    setGlobalSettingsAccountAndRunOOBE?: (
        sourceId: string,
        origin: AccountActionOrigin
    ) => Promise<boolean>;
    checkM365Installation?: () => Promise<M365InstallationCheckResult>;
    remediateAllLicensingAccounts?: (isSilent: boolean) => Promise<LicensingRemediationResult[]>;
    remediateLicensingMailbox?: (
        licensingMailbox: LicensingMailboxInfo,
        isSilent: boolean
    ) => Promise<LicensingRemediationResult>;
    buildLinkedLicensingMailboxInfo?: (
        account: CoprincipalAccount,
        sourceId: string
    ) => LicensingMailboxInfo | undefined;
    tryGetTokenSilently?: (
        mailboxInfo: MailboxInfo,
        sourceType: AccountSourceType
    ) => Promise<string | undefined>;
    getHiddenAutomappedMailboxesForAccount?: (sourceId: string) => AutomappedMailbox[] | undefined;
    hideAutomappedMailboxForAccount?: (sourceId: string, display: string, email: string) => void;
    unhideAutomappedMailboxforAccount?: (sourceId: string, email: string) => void;
    doesSharedMailboxHaveFullAccess?: (mailboxInfo: MailboxInfo) => Promise<boolean>;
    checkPstPrerequisites?: () => Promise<PstPrerequisitesStatus>;
    checkBulkTransferPrerequisites?: () => Promise<BulkTransferPrerequisitesStatus>;
    checkExportToPstPrerequisites?: () => Promise<ExportToPstPrerequisitesStatus>;
    replaceAddedAccount?: (account: CoprincipalAccount, actionOrigin: AccountActionOrigin) => void;
}

// The pluggable host instance
let thePluggableHost: PluggableHostAccountSource = {};
let lastPluggableHostStack: string | undefined = undefined;

/**
 * Sets the pluggable host
 * @param host provides the host specific implementation for getting account information
 */
export function updatePluggableHost(host?: Partial<PluggableHostAccountSource>): void {
    thePluggableHost = { ...thePluggableHost, ...host };
    lastPluggableHostStack = new Error('UpdatePluggableHost').stack;
}

/**
 * Called wheneverr a pluggable host method is not set
 * @param methodName Name of the method that was not set
 */
function reportPluggableHostMethodNotFound(methodName: string): void {
    /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
     * BASELINE: DO NOT COPY/PASTE!
     *	> Argument at index 1 for logGreyErrorFromAccounts must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */
    logGreyErrorFromAccounts(
        'PluggableMethodNotSet',
        undefined,
        new Error('PluggableMethodNotSet'),
        {
            isStoreInitialized: isAccountSourceListStoreInitialized(),
            wasUpdated: lastPluggableHostStack != undefined,
            lastPluggableHostStack,
            methodName,
        }
    );
}

/**
 * Restores the pluggable host to the default pluggable host
 */
export function testHookToResetPluggableHost(): void {
    thePluggableHost = {};
}

/**
 * Builds linked licensing mailbox info for a coprincipal account.
 * Returns undefined by default if not injected by the host.
 * @param account The coprincipal account
 * @param sourceId The source ID for the account
 * @returns LicensingMailboxInfo if available, undefined otherwise
 */
export function buildLinkedLicensingMailboxInfo(
    account: CoprincipalAccount,
    sourceId: string
): LicensingMailboxInfo | undefined {
    if (thePluggableHost.buildLinkedLicensingMailboxInfo) {
        return thePluggableHost.buildLinkedLicensingMailboxInfo(account, sourceId);
    }
    return undefined;
}

/**
 * Adds a Microsoft 365 enterprise account
 * @param email Email address of the mailbox to be connected to
 * @param userIdentity The user principal named that identifies how we will be authenticating
 * @param origin The source of the request to add the Microsoft 365 account
 * @returns The result of creating the account
 */
export async function addM365Account(
    correlationId: string,
    email: string,
    userIdentity: string,
    origin: AccountActionOrigin,
    authUxCallbacks?: AuthUXCallbacks,
    isAnotherMailbox?: boolean,
    silentAuthToken?: string,
    skipAccountPolicyChecks?: boolean,
    cloudEnvironmentInfo?: NewAccountCloudEnvironmentInfo
): Promise<CreateAccountResult> {
    if (!thePluggableHost.addM365Account) {
        errorThatWillCauseAlertAndThrow('NoPluggableMethodAddM365Account');
    }

    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion  -- (https://aka.ms/OWALintWiki)
     * Non-null assertions are dangerous, as they can hide bugs from strictness checks. Please remove this usage or replace this line with a justification.
     *	> Forbidden non-null assertion. */
    return thePluggableHost.addM365Account!(
        correlationId,
        email,
        userIdentity,
        origin,
        authUxCallbacks,
        isAnotherMailbox,
        silentAuthToken,
        skipAccountPolicyChecks,
        cloudEnvironmentInfo
    );
}

export async function addOutlookDotComAccount(
    correlationId: string,
    email: string,
    origin: AccountActionOrigin,
    authUxCallbacks?: AuthUXCallbacks,
    silentAuthToken?: string
): Promise<CreateAccountResult> {
    if (!thePluggableHost.addOutlookDotComAccount) {
        errorThatWillCauseAlertAndThrow('NoPluggableMethodAddOutlookDotComAccount');
    }

    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion  -- (https://aka.ms/OWALintWiki)
     * Non-null assertions are dangerous, as they can hide bugs from strictness checks. Please remove this usage or replace this line with a justification.
     *	> Forbidden non-null assertion. */
    return thePluggableHost.addOutlookDotComAccount!(
        correlationId,
        email,
        origin,
        authUxCallbacks,
        silentAuthToken
    );
}

export async function addGoogleAccount(
    correlationId: string,
    email: string,
    origin: AccountActionOrigin,
    authUxCallbacks?: AuthUXCallbacks
): Promise<CreateAccountResult> {
    if (!thePluggableHost.addGoogleAccount) {
        errorThatWillCauseAlertAndThrow('NoPluggableMethodAddGoogleAccount');
    }

    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion  -- (https://aka.ms/OWALintWiki)
     * Non-null assertions are dangerous, as they can hide bugs from strictness checks. Please remove this usage or replace this line with a justification.
     *	> Forbidden non-null assertion. */
    return thePluggableHost.addGoogleAccount!(correlationId, email, origin, authUxCallbacks);
}

export async function addYahooAccount(
    correlationId: string,
    email: string,
    origin: AccountActionOrigin,
    authUxCallbacks?: AuthUXCallbacks
): Promise<CreateAccountResult> {
    if (!thePluggableHost.addYahooAccount) {
        errorThatWillCauseAlertAndThrow('NoPluggableMethodAddYahooAccount');
    }

    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion  -- (https://aka.ms/OWALintWiki)
     * Non-null assertions are dangerous, as they can hide bugs from strictness checks. Please remove this usage or replace this line with a justification.
     *	> Forbidden non-null assertion. */
    return thePluggableHost.addYahooAccount!(correlationId, email, origin, authUxCallbacks);
}

export async function addImapAccount(
    correlationId: string,
    email: string,
    origin: AccountActionOrigin,
    protocolData: AccountSourceProtocolData[],
    authUxCallbacks?: AuthUXCallbacks
): Promise<CreateAccountResult> {
    if (!thePluggableHost.addImapAccount) {
        errorThatWillCauseAlertAndThrow('NoPluggableMethodAddImapAccount');
    }

    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion  -- (https://aka.ms/OWALintWiki)
     * Non-null assertions are dangerous, as they can hide bugs from strictness checks. Please remove this usage or replace this line with a justification.
     *	> Forbidden non-null assertion. */
    return thePluggableHost.addImapAccount!(
        correlationId,
        email,
        origin,
        protocolData,
        authUxCallbacks
    );
}

export async function addPop3Account(
    correlationId: string,
    email: string,
    origin: AccountActionOrigin,
    protocolData: AccountSourceProtocolData[],
    authUxCallbacks?: AuthUXCallbacks
): Promise<CreateAccountResult> {
    if (!thePluggableHost.addPop3Account) {
        errorThatWillCauseAlertAndThrow('NoPluggableMethodAddPop3Account');
    }

    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion  -- (https://aka.ms/OWALintWiki)
     * Non-null assertions are dangerous, as they can hide bugs from strictness checks. Please remove this usage or replace this line with a justification.
     *	> Forbidden non-null assertion. */
    return thePluggableHost.addPop3Account!(
        correlationId,
        email,
        origin,
        protocolData,
        authUxCallbacks
    );
}

export async function addICloudAccount(
    correlationId: string,
    email: string,
    origin: AccountActionOrigin,
    protocolData?: AccountSourceProtocolData[],
    authUxCallbacks?: AuthUXCallbacks,
    authType?: AccountAuthType
): Promise<CreateAccountResult> {
    if (!thePluggableHost.addICloudAccount) {
        errorThatWillCauseAlertAndThrow('NoPluggableMethodAddiCloudAccount');
    }

    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion  -- (https://aka.ms/OWALintWiki)
     * Non-null assertions are dangerous, as they can hide bugs from strictness checks. Please remove this usage or replace this line with a justification.
     *	> Forbidden non-null assertion. */
    return thePluggableHost.addICloudAccount!(
        correlationId,
        email,
        origin,
        protocolData,
        authUxCallbacks,
        authType
    );
}

export async function addPstFileAccount(
    correlationId: string,
    filepath: string,
    displayName: string,
    origin: AccountActionOrigin,
    authUxCallbacks?: AuthUXCallbacks
): Promise<CreateAccountResult> {
    if (!thePluggableHost.addPstFileAccount) {
        errorThatWillCauseAlertAndThrow('NoPluggableMethodAddPstFileAccount');
    }

    return (
        thePluggableHost?.addPstFileAccount(
            correlationId,
            filepath,
            displayName,
            origin,
            authUxCallbacks
        ) ??
        Promise.resolve({
            wasSuccessful: false,
            error: {
                error: CreateAccountErrors.ProviderNotSupported,
            },
        })
    );
}

export async function removeAccount(
    sourceId: string,
    origin: AccountActionOrigin
): Promise<RemoveAccountResult> {
    if (!thePluggableHost.removeAccount) {
        errorThatWillCauseAlertAndThrow('NoPluggableMethodRemoveAccount');
    }

    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion  -- (https://aka.ms/OWALintWiki)
     * Non-null assertions are dangerous, as they can hide bugs from strictness checks. Please remove this usage or replace this line with a justification.
     *	> Forbidden non-null assertion. */
    return thePluggableHost.removeAccount!(sourceId, origin);
}

export async function removePendingAccount(
    emailAddress: string,
    userIdentity: string,
    origin: AccountActionOrigin,
    accountType: AccountSourceType
): Promise<RemoveAccountResult> {
    if (!thePluggableHost.removePendingAccount) {
        errorThatWillCauseAlertAndThrow('NoPluggableMethodRemoveAccount');
    }

    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion  -- (https://aka.ms/OWALintWiki)
     * Non-null assertions are dangerous, as they can hide bugs from strictness checks. Please remove this usage or replace this line with a justification.
     *	> Forbidden non-null assertion. */
    return thePluggableHost.removePendingAccount!(emailAddress, userIdentity, origin, accountType);
}

export async function disablePendingAccount(
    emailAddress: string,
    userIdentity: string,
    origin: AccountActionOrigin,
    accountType: AccountSourceType
): Promise<RemoveAccountResult> {
    if (!thePluggableHost.disablePendingAccount) {
        errorThatWillCauseAlertAndThrow('NoPluggableMethodDisableAccount');
    }

    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion  -- (https://aka.ms/OWALintWiki)
     * Non-null assertions are dangerous, as they can hide bugs from strictness checks. Please remove this usage or replace this line with a justification.
     *	> Forbidden non-null assertion. */
    return thePluggableHost.disablePendingAccount!(emailAddress, userIdentity, origin, accountType);
}

export async function createAndAddOutlookDotComAccount(
    correlationId: string,
    origin: AccountActionOrigin,
    authUxCallbacks?: AuthUXCallbacks
): Promise<CreateAccountResult> {
    if (!thePluggableHost.createAndAddOutlookDotComAccount) {
        errorThatWillCauseAlertAndThrow('NoPluggableMethodAddOutlookDotComAccount');
    }

    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion  -- (https://aka.ms/OWALintWiki)
     * Non-null assertions are dangerous, as they can hide bugs from strictness checks. Please remove this usage or replace this line with a justification.
     *	> Forbidden non-null assertion. */
    return thePluggableHost.createAndAddOutlookDotComAccount!(
        correlationId,
        origin,
        authUxCallbacks
    );
}

export function isSettingGlobalSettingsAccountAllowed(): boolean {
    return !!thePluggableHost.setGlobalSettingsAccountAndRunOOBE;
}

export function setGlobalSettingsAccountAndRunOOBE(
    sourceId: string,
    origin: AccountActionOrigin
): Promise<boolean> {
    if (thePluggableHost.setGlobalSettingsAccountAndRunOOBE) {
        return thePluggableHost.setGlobalSettingsAccountAndRunOOBE(sourceId, origin);
    }

    errorThatWillCauseAlert('NoPluggableMethodSetGlobalSettingsAccountAndRunOOBE');
    return Promise.resolve(false);
}

/**
 * When the account cannot be found when getting the persistence Id we raise an alert
 * and include diagnostic data about the MailboxInfo.
 * @param mailboxInfo MailboxInfo for which an account could not be found
 */
function alertOnAccountNotFound(mailboxInfo: MailboxInfo): void {
    const accountDiagnostic = getAccountDiagnosticDataForMailboxInfo(mailboxInfo);
    const info = {
        mailboxType: mailboxInfo?.type,
        hasUserId: !!mailboxInfo?.userIdentity,
        hasSmtp: !!mailboxInfo?.mailboxSmtpAddress,
        rank: mailboxInfo?.mailboxRank,
        isAnonymous: mailboxInfo?.isAnonymous,
        isRemoved: mailboxInfo?.isRemoved,
        ...accountDiagnostic,
    };

    const error: TraceErrorObject = new Error('FailedToFindCoprincipalAccount');
    error.additionalInfo = info;
    errorThatWillCauseAlert(error);
}

export function getCoprincipalAccountPersistenceId(
    mailboxInfo: MailboxInfo,
    noAlertOnFailure?: boolean
): string {
    // When the account key is based on the persistenceId we can just get the account key
    const persistenceId = getAccountKeyForMailboxInfo(mailboxInfo);
    if (isValidAccountKey(persistenceId)) {
        return persistenceId;
    }

    if (!noAlertOnFailure) {
        alertOnAccountNotFound(mailboxInfo);
    }

    return '';
}

export function checkM365Installation(): Promise<M365InstallationCheckResult> {
    if (!thePluggableHost.checkM365Installation) {
        errorThatWillCauseAlert('NoPluggableMethodCheckM365Installation');
        return Promise.resolve({
            hasOutlookMapiInstalled: false,
            targetArch: undefined,
            checkedForOutlookMapiWithMismatchedArch: false,
            outlookMapiArch: undefined,
            hasMapiInstalled: undefined,
            mapiArch: undefined,
        });
    }

    return thePluggableHost.checkM365Installation();
}

export function remediateAllLicensingAccounts(
    isSilent: boolean
): Promise<LicensingRemediationResult[]> {
    if (!thePluggableHost.remediateAllLicensingAccounts) {
        errorThatWillCauseAlert('NoPluggableMethodRemediateAllLicensingAccounts');
        throw new Error('NoPluggableMethodRemediateAllLicensingAccounts');
    }

    return thePluggableHost.remediateAllLicensingAccounts(isSilent);
}

export function remediateLicensingMailbox(
    licensingMailbox: LicensingMailboxInfo,
    isSilent: boolean
): Promise<LicensingRemediationResult> {
    if (!thePluggableHost.remediateLicensingMailbox) {
        errorThatWillCauseAlert('NoPluggableMethodRemediateLicensingMailbox');
        throw new Error('NoPluggableMethodRemediateAllLicensingAccounts');
    }

    return thePluggableHost.remediateLicensingMailbox(licensingMailbox, isSilent);
}

export function tryGetTokenSilently(
    mailboxInfo: MailboxInfo,
    sourceType: AccountSourceType
): Promise<string | undefined> {
    if (thePluggableHost.tryGetTokenSilently) {
        return thePluggableHost.tryGetTokenSilently(mailboxInfo, sourceType);
    }

    errorThatWillCauseAlert('NoPluggableMethodSetGlobalSettingsAccountAndRunOOBE');
    return Promise.resolve('');
}

export function getHiddenAutomappedMailboxes(sourceId: string): AutomappedMailbox[] | undefined {
    return thePluggableHost.getHiddenAutomappedMailboxesForAccount?.(sourceId);
}

/**
 * Hides the specified automapped mailbox and removes it from the store
 * @param sourceId sourceId of the account
 * @param display Display name of the automapped mailbox
 * @param email Email address of the automapped mailbox
 */
export function hideAutomappedMailbox(sourceId: string, display: string, email: string): void {
    if (thePluggableHost.hideAutomappedMailboxForAccount) {
        thePluggableHost.hideAutomappedMailboxForAccount(sourceId, display, email);
        // remove the automapped mailbox from the store
        removeAutomappedSharedMailbox(sourceId, email);
    }
}

/**
 * Remove the hidding of the automapped mailbox, to get the mailbox back into the store
 * the ensureSharedMailboxData will need to be called for forceUpdate as true
 * @param sourceId SourcId of the account
 * @param email Email of the automapped shared mailbox to be unhidden
 */
export function unhideAutomappedMailbox(sourceId: string, email: string): void {
    thePluggableHost.unhideAutomappedMailboxforAccount?.(sourceId, email);
}

/**
 *
 * @param mailboxInfo
 * @returns
 */
export function doesSharedMailboxHaveFullAccess(mailboxInfo: MailboxInfo): Promise<boolean> {
    if (thePluggableHost.doesSharedMailboxHaveFullAccess) {
        return thePluggableHost.doesSharedMailboxHaveFullAccess(mailboxInfo);
    }

    // Report that we do not have a pluggable host method
    reportPluggableHostMethodNotFound('doesSharedMailboxHaveFullAccess');
    return Promise.resolve(false);
}

/**
 * Checks all of the prerequisites to open PST files
 * @returns Summary of prerequisite check results
 */
export function checkPstPrerequisites(): Promise<PstPrerequisitesStatus> {
    if (thePluggableHost.checkPstPrerequisites) {
        return thePluggableHost.checkPstPrerequisites();
    }

    errorThatWillCauseAlertAndThrow('NoPluggableMethodCheckPstPrerequisites');
}

/**
 * Checks all of the prerequisites to use the bulk transfer engine
 * @returns Summary of prerequisite check results
 */
export function checkBulkTransferPrerequisites(): Promise<BulkTransferPrerequisitesStatus> {
    if (thePluggableHost.checkBulkTransferPrerequisites) {
        return thePluggableHost.checkBulkTransferPrerequisites();
    }

    reportPluggableHostMethodNotFound('checkBulkTransferPrerequisites');
    return Promise.resolve({
        overallResult: 'PluggableHostMethodNotSet_checkBulkTransferPrerequisites',
        toLoggable: () => {
            return { overallResult: 'PluggableHostMethodNotSet_checkBulkTransferPrerequisites' };
        },
    });
}

/**
 * Checks all of the prerequisites to export mailbox content to a PST file
 * @returns Summary of prerequisite check results
 */
export function checkExportToPstPrerequisites(): Promise<ExportToPstPrerequisitesStatus> {
    if (thePluggableHost.checkExportToPstPrerequisites) {
        return thePluggableHost.checkExportToPstPrerequisites();
    }

    reportPluggableHostMethodNotFound('checkExportToPstPrerequisites');
    return Promise.resolve({
        overallResult: 'PluggableHostMethodNotSet_checkExportToPstPrerequisites',
        toLoggable: () => {
            return { overallResult: 'PluggableHostMethodNotSet_checkExportToPstPrerequisites' };
        },
    });
}

export function replaceAddedAccount(
    account: CoprincipalAccount,
    actionOrigin: AccountActionOrigin
): void {
    if (thePluggableHost.replaceAddedAccount) {
        thePluggableHost.replaceAddedAccount(account, actionOrigin);
        return;
    }

    errorThatWillCauseAlert('NoPluggableMethodReplaceAddedAccount');
}
